import type { OtpProvider, OtpSettings } from '@erp/shared';
import { CheckCircle2, MessageCircle, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import { Button, FormField } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';

const GREEN_DEFAULT_URL = 'https://api.green-api.com';
const EVOLUTION_DEFAULT_URL = 'https://wa.vylo-tech.com';

/**
 * Phone OTP login settings. Delivers one-time codes over WhatsApp via the
 * tenant's gateway — Green API (cloud) or the self-hosted Evolution API
 * (instance name + per-instance token from Platform → WhatsApp). The token is
 * stored encrypted and never returned. "Test connection" checks the gateway is
 * authorized/connected.
 */
export function OtpSettingsPage(): JSX.Element {
  const { t, i18n } = useTranslation('otp');
  const { t: tc } = useTranslation();
  const [s, setS] = useState<OtpSettings | null>(null);
  const [provider, setProvider] = useState<OtpProvider>('greenapi');
  const [instanceId, setInstanceId] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [apiUrl, setApiUrl] = useState(GREEN_DEFAULT_URL);
  const [messageTemplate, setMessageTemplate] = useState('Your verification code is {code}');
  const [codeLength, setCodeLength] = useState('6');
  const [ttlSeconds, setTtlSeconds] = useState('300');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const hydrate = (v: OtpSettings): void => {
    setS(v);
    setProvider(v.provider);
    setInstanceId(v.instanceId ?? '');
    setApiUrl(v.apiUrl);
    setMessageTemplate(v.messageTemplate);
    setCodeLength(String(v.codeLength));
    setTtlSeconds(String(v.ttlSeconds));
    setIsActive(v.isActive);
  };
  useEffect(() => { void api().get<OtpSettings>('/otp/settings').then((r) => hydrate(r.data)).catch(() => undefined); }, []);

  const isEvolution = provider === 'evolution';

  /** Swap the URL default along with the provider (only when it's still the other's default). */
  const switchProvider = (next: OtpProvider): void => {
    setProvider(next);
    if (next === 'evolution' && apiUrl.trim() === GREEN_DEFAULT_URL) setApiUrl(EVOLUTION_DEFAULT_URL);
    if (next === 'greenapi' && apiUrl.trim() === EVOLUTION_DEFAULT_URL) setApiUrl(GREEN_DEFAULT_URL);
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        provider,
        instanceId: instanceId.trim() || null,
        apiUrl: apiUrl.trim(),
        messageTemplate: messageTemplate.trim(),
        codeLength: Number(codeLength) || 6,
        ttlSeconds: Number(ttlSeconds) || 300,
        isActive,
      };
      if (apiToken.trim()) body.apiToken = apiToken.trim();
      const r = await api().put<OtpSettings>('/otp/settings', body);
      hydrate(r.data);
      setApiToken('');
      toast.success(tc('actions.save', { defaultValue: 'Saved' }));
    } catch { toast.error(tc('errors:server')); } finally { setSaving(false); }
  };

  const test = async (): Promise<void> => {
    setTesting(true);
    try { await api().post('/otp/test', {}); toast.success(t('tested', { defaultValue: 'Connection OK — the WhatsApp gateway is authorized.' })); }
    catch { toast.error(t('testFailed', { defaultValue: 'Gateway not reachable. Check the instance id / token.' })); }
    finally { setTesting(false); }
  };

  if (!s) return <Card className="p-6"><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <MessageCircle className="h-6 w-6 text-brand" aria-hidden />{t('title', { defaultValue: 'Phone login' })}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">{t('subtitle', { defaultValue: 'Let customers sign in with their phone number. Codes are sent over WhatsApp via your gateway.' })}</p>
      </header>

      {/*
        A code that never arrives looks like nothing at all from here: the send
        path hides gateway failures from the caller on purpose, so this row is
        the shop's only view of whether the last one actually left. A stale
        token — the usual cause, after the WhatsApp number is re-linked — shows
        up as a refusal here.
      */}
      {s.lastSendAt ? (
        <Card className={`flex items-start gap-3 p-4 text-sm ${s.lastSendError ? 'border-danger/40' : ''}`}>
          {s.lastSendError
            ? <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
            : <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />}
          <div className="flex flex-col gap-1">
            <span className="font-medium">
              {s.lastSendError
                ? t('lastSendFailed', { defaultValue: 'The gateway refused the last code.' })
                : t('lastSendOk', { defaultValue: 'The last code was handed to the gateway.' })}
            </span>
            <span className="text-fg-muted">{formatDateTime(s.lastSendAt, { locale: i18n.language })}</span>
            {s.lastSendError ? <code className="text-xs text-fg-muted">{s.lastSendError}</code> : null}
            {s.lastSendError ? (
              <span className="text-fg-muted">
                {t('lastSendFailedHint', { defaultValue: 'Re-linking the WhatsApp number creates a new instance token — paste the current one above and save.' })}
              </span>
            ) : null}
          </div>
        </Card>
      ) : null}

      <Card className="flex flex-col gap-4 p-6">
        <FormField id="otp-provider" label={t('provider', { defaultValue: 'Gateway provider' })}>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={isEvolution ? 'secondary' : 'primary'}
              onClick={() => switchProvider('greenapi')}
            >
              {t('providerGreen', { defaultValue: 'Green API' })}
            </Button>
            <Button
              type="button"
              variant={isEvolution ? 'primary' : 'secondary'}
              onClick={() => switchProvider('evolution')}
            >
              {t('providerEvolution', { defaultValue: 'Vylo WhatsApp (self-hosted)' })}
            </Button>
          </div>
        </FormField>
        <FormField
          id="otp-inst"
          label={isEvolution ? t('instanceName', { defaultValue: 'Instance name' }) : t('instanceId', { defaultValue: 'Green API instance ID' })}
          helperText={isEvolution ? t('instanceNameHint', { defaultValue: 'The gateway instance that owns the WhatsApp number (Platform → WhatsApp).' }) : undefined}
        >
          <Input id="otp-inst" value={instanceId} onChange={(e) => setInstanceId(e.target.value)} placeholder={isEvolution ? 'duhok-otp' : '1101000001'} />
        </FormField>
        <FormField
          id="otp-tok"
          label={isEvolution ? t('instanceToken', { defaultValue: 'Instance token (apikey)' }) : t('apiToken', { defaultValue: 'API token' })}
          helperText={s.hasToken ? t('tokenSet', { defaultValue: 'A token is saved — leave blank to keep it.' }) : isEvolution ? t('evoTokenHint', { defaultValue: 'Copy it from the instance card in Platform → WhatsApp. Stored encrypted.' }) : t('tokenHint', { defaultValue: 'Stored encrypted; never shown again.' })}
        >
          <Input id="otp-tok" type="password" value={apiToken} onChange={(e) => setApiToken(e.target.value)} placeholder={s.hasToken ? '••••••••' : ''} autoComplete="new-password" />
        </FormField>
        <FormField
          id="otp-url"
          label={isEvolution ? t('gatewayUrl', { defaultValue: 'Gateway URL' }) : t('apiUrl', { defaultValue: 'API base URL' })}
          helperText={isEvolution ? t('gatewayUrlHint', { defaultValue: 'Your WhatsApp gateway, e.g. https://wa.vylo-tech.com' }) : t('apiUrlHint', { defaultValue: 'Green API host, e.g. https://api.green-api.com' })}
        >
          <Input id="otp-url" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} />
        </FormField>
        <FormField id="otp-msg" label={t('template', { defaultValue: 'Message template' })} helperText={t('templateHint', { defaultValue: 'Use {code} where the code should appear.' })}>
          <textarea
            id="otp-msg"
            rows={2}
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={messageTemplate}
            onChange={(e) => setMessageTemplate(e.target.value)}
          />
        </FormField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="otp-len" label={t('codeLength', { defaultValue: 'Code length' })}>
            <Input id="otp-len" type="number" min={4} max={8} value={codeLength} onChange={(e) => setCodeLength(e.target.value)} />
          </FormField>
          <FormField id="otp-ttl" label={t('ttl', { defaultValue: 'Code lifetime (seconds)' })} helperText={t('ttlHint', { defaultValue: 'How long a code stays valid (60–900).' })}>
            <Input id="otp-ttl" type="number" min={60} max={900} value={ttlSeconds} onChange={(e) => setTtlSeconds(e.target.value)} />
          </FormField>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 accent-[var(--color-brand)]" />
          {t('active', { defaultValue: 'Offer phone login on the storefront' })}
        </label>
        <div className="flex gap-2">
          <Button onClick={save} loading={saving}>{tc('actions.save')}</Button>
          <Button variant="secondary" onClick={test} loading={testing} disabled={!s.hasToken && !apiToken.trim()}>
            <ShieldCheck className="h-4 w-4" aria-hidden />{t('test', { defaultValue: 'Test connection' })}
          </Button>
        </div>
      </Card>
    </div>
  );
}
